<template>
  <div class="volume-controls">
    <div class="volume-controls__mute-toggle-pulse" v-show="muted"/>
    <div class="volume-controls__mute-toggle"
      id="muteToggle"
      @click="onToggleMute"
      @click.once="unlockAudioContext">
      <mute-icon v-show="muted" />
      <unmute-icon v-show="!muted" />
    </div>
    <div class="volume-controls__slider-wrapper">
      <slider 
        :min="0" 
        :max="1" 
        :step="0.01" 
        :modelValue="volumeGain"
        @update:modelValue="(value) => onUpdateSliderGain(value)"
        @change="updateLastSliderGain()" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import MuteIcon from '@/assets/MuteIcon.svg'
import UnmuteIcon from '@/assets/UnmuteIcon.svg'
import useAudioEngine from '@/composables/UseAudioEngine'
import Slider from '@/components/common/Slider.vue'

const { unlockAudioContext, muteAudio, unmuteAudio, volumeGain, setGainValue } = useAudioEngine()
let lastSliderGainValue = volumeGain.value

const onUpdateSliderGain = (value: number): void => {
  setGainValue(value)
  volumeGain.value = value
  if (muted.value) {
    muteAudio()
  } else {
    unmuteAudio()
  }
}

const muted = computed(() => {
  return volumeGain.value === 0
})

const updateLastSliderGain = () => {
  if (volumeGain.value !== 0) {
    lastSliderGainValue = volumeGain.value
  }
}

const onToggleMute = () => {
  volumeGain.value = muted.value ? lastSliderGainValue : 0
  setGainValue(volumeGain.value)
  if (muted.value) {
    muteAudio()
  } else {
    unmuteAudio()
  }
}
</script>

<style lang="scss">
.volume-controls {
  display: flex;
  align-items: center;

  &:hover {
    .volume-controls__slider-wrapper {
      width: 104px;

      .slider {
        display:block;
      }
    }
  }

  &__mute-toggle {
    display: flex;
    align-items: center;
    cursor: pointer;
    border-radius: 16px;
    width: 32px;
    height: 32px;
    background-color: $purple;
    z-index: 1;
    transition: background-color 150ms $animationCurve;

    &:hover {
      background-color: $purple-light;
    }

    > svg {
      margin-left: 8px;
    }
  }

  &__mute-toggle-pulse {
    position: absolute;
    height: 32px;
    width: 32px;
    border-radius: 50%;
    background-color: $white;
    opacity: 100%;
    animation: pulse 2400ms $animationCurve infinite;
    pointer-events: none;
  }

  &__slider-wrapper {
    display: flex;
    width: 12px;
    overflow: hidden;
    transition: width 100ms $animationCurve;

    .slider {
      display: none;
      margin: 12px;
      width: 80px;
      height: 3px;
      &::-webkit-slider-thumb {
        width: 15px;
        height: 15px;
      }
    }
  }

  @keyframes pulse {
    83%, 100% {
      transform: scale(calc(70 / 32));
      opacity: 0%;
    }
  }
}
</style>
